/**
 * Skill Center — admin / approver view of all team skills + dashboard.
 *
 * Tabs:
 *   - Dashboard: graphs / aggregates (uses /workspaces/{ws}/skills/analytics)
 *   - All skills: searchable / filterable directory of every team skill record
 *   - Approvals: queue of pending / edit_pending skill records to approve or reject
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import PageHeader from "@/components/PageHeader";
import { Award, Search, CheckCircle2, XCircle, Clock, BarChart3, Users, Star, Filter, TrendingUp, Layers, Target } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { StatusBadge, RatingStars, StarPicker } from "@/pages/app/MyDashboard";
import AdvancedTable from "@/components/table/AdvancedTable";
import { apiErrorMessage } from "@/lib/apiError";

export default function SkillCenter() {
  const { activeWorkspaceId } = useAuth();
  const { can } = useWorkspacePerms();
  const [sp, setSp] = useSearchParams();
  const initialTab = sp.get("tab") || "dashboard";
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    setSp((prev) => { const n = new URLSearchParams(prev); n.set("tab", tab); return n; }, { replace: true });
  }, [tab, setSp]);

  // Permission gate at page level — anyone with skill.view_all (or admin) can see this page.
  // Approve tab additionally needs skill.approve.

  return (
    <div className="space-y-6" data-testid="skill-center-page">
      <PageHeader
        title="Skill center"
        subtitle="See the skills mix across your team, approve new records and spot capability gaps."
        icon={Award}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none flex-wrap h-auto bg-transparent p-0 border-b border-border w-full justify-start gap-1">
          <TabsTrigger value="dashboard" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="skill-center-tab-dashboard"><BarChart3 className="w-4 h-4 mr-2" />Dashboard</TabsTrigger>
          <TabsTrigger value="all" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="skill-center-tab-all"><Layers className="w-4 h-4 mr-2" />All skills</TabsTrigger>
          <TabsTrigger value="approvals" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="skill-center-tab-approvals"><CheckCircle2 className="w-4 h-4 mr-2" />Approvals</TabsTrigger>
          <TabsTrigger value="gaps" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="skill-center-tab-gaps"><Target className="w-4 h-4 mr-2" />Skill gap</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-6"><DashboardTab wsId={activeWorkspaceId} /></TabsContent>
        <TabsContent value="all" className="mt-6"><AllSkillsTab wsId={activeWorkspaceId} canApprove={can("skill.approve")} /></TabsContent>
        <TabsContent value="approvals" className="mt-6"><ApprovalsTab wsId={activeWorkspaceId} canApprove={can("skill.approve")} /></TabsContent>
        <TabsContent value="gaps" className="mt-6"><SkillGapTab wsId={activeWorkspaceId} canEdit={can("skill.approve")} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ───────────────────────── Dashboard ─────────────────────────

function DashboardTab({ wsId }) {
  const [data, setData] = useState(null);
  const [userFilter, setUserFilter] = useState("active");
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!wsId) return;
    setData(null);
    api.get(`/workspaces/${wsId}/skills/analytics?user_filter=${userFilter}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load analytics"));
  }, [wsId, userFilter]);

  if (err) return <Card className="rounded-none"><CardContent className="p-6 text-sm text-destructive" data-testid="dashboard-error">{err}</CardContent></Card>;
  if (!data) return <Card className="rounded-none"><CardContent className="p-6 text-sm text-foreground/60">Loading…</CardContent></Card>;

  return (
    <div className="space-y-5" data-testid="skill-dashboard">
      {/* User filter */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1">
          {["active", "inactive", "all"].map((v) => (
            <button
              key={v}
              onClick={() => setUserFilter(v)}
              className={`text-xs px-3 py-1.5 border rounded-none capitalize ${userFilter === v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-foreground/5"}`}
              data-testid={`skill-dash-filter-${v}`}
            >
              {v} users
            </button>
          ))}
        </div>
        <span className="text-xs text-foreground/55">Charts show <strong>approved</strong> skill records only.</span>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <DashStat icon={Award} label="Total skills" value={data.summary.total_skills} testid="dash-stat-total" />
        <DashStat icon={Layers} label="Unique skills" value={data.summary.unique_skills} testid="dash-stat-unique" />
        <DashStat icon={Users} label="People with skills" value={data.summary.unique_users} testid="dash-stat-users" />
        <DashStat icon={Filter} label="Categories" value={data.summary.categories} testid="dash-stat-categories" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="rounded-none">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold text-sm flex items-center gap-2"><Layers className="w-4 h-4 text-foreground/60" />By category</h4>
              <span className="text-[10px] uppercase tracking-widest text-foreground/55">count · avg rating</span>
            </div>
            <BarList items={data.by_category} labelKey="category" testid="dash-by-category" />
          </CardContent>
        </Card>

        <Card className="rounded-none">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4 text-foreground/60" />Top skills</h4>
              <span className="text-[10px] uppercase tracking-widest text-foreground/55">{data.top_skills.length} shown</span>
            </div>
            <BarList items={data.top_skills} labelKey="skill_name" testid="dash-top-skills" />
          </CardContent>
        </Card>

        <Card className="rounded-none">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold text-sm flex items-center gap-2"><Star className="w-4 h-4 text-foreground/60" />Rating distribution</h4>
            </div>
            <RatingHistogram data={data.rating_distribution} />
          </CardContent>
        </Card>

        <Card className="rounded-none">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold text-sm flex items-center gap-2"><Users className="w-4 h-4 text-foreground/60" />Top contributors</h4>
            </div>
            {data.top_contributors.length === 0 ? (
              <p className="text-xs text-foreground/55" data-testid="dash-contributors-empty">No approved skills yet.</p>
            ) : (
              <ul className="divide-y divide-border" data-testid="dash-contributors">
                {data.top_contributors.map((c) => (
                  <li key={c.user_id} className="py-2 flex items-center gap-3 text-sm" data-testid={`dash-contrib-${c.user_id}`}>
                    <div className="w-7 h-7 bg-foreground/10 flex items-center justify-center text-xs font-bold rounded-none">{(c.name || "?").slice(0, 1).toUpperCase()}</div>
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{c.name}</div>
                      <div className="text-[10px] text-foreground/50 truncate">{c.email}</div>
                    </div>
                    <span className="text-xs font-mono">{c.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Status mix */}
      <Card className="rounded-none">
        <CardContent className="p-5">
          <h4 className="font-bold text-sm mb-3">Approval pipeline</h4>
          <div className="flex flex-wrap gap-3 text-sm" data-testid="dash-status-mix">
            {data.status_mix.length === 0 && <span className="text-foreground/55 text-xs">No skill records yet.</span>}
            {data.status_mix.map((s) => (
              <div key={s.status} className="border border-border px-3 py-2 flex items-center gap-2" data-testid={`dash-status-${s.status}`}>
                <StatusBadge status={s.status} />
                <span className="font-mono text-xs">{s.count}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function DashStat({ icon: Icon, label, value, testid }) {
  return (
    <Card className="rounded-none" data-testid={testid}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className="w-9 h-9 border border-border flex items-center justify-center text-foreground/60">
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-foreground/55">{label}</div>
          <div className="text-2xl font-bold leading-none mt-1">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function BarList({ items, labelKey, testid }) {
  if (!items.length) return <p className="text-xs text-foreground/55" data-testid={`${testid}-empty`}>No data yet.</p>;
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <ul className="space-y-2" data-testid={testid}>
      {items.map((it) => {
        const pct = (it.count / max) * 100;
        return (
          <li key={it[labelKey]} className="text-sm" data-testid={`${testid}-row-${(it[labelKey] || "").toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="truncate font-medium">{it[labelKey]}</span>
              <span className="text-foreground/60 font-mono">
                {it.count}{it.avg_rating ? ` · ${it.avg_rating}★` : ""}
              </span>
            </div>
            <div className="h-2 bg-foreground/10">
              <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function RatingHistogram({ data }) {
  const max = Math.max(...data.map((d) => d.count), 1);
  const totalRated = data.reduce((s, d) => s + d.count, 0);
  return (
    <div data-testid="dash-rating-hist">
      <div className="flex items-end gap-2 h-32">
        {data.map((d) => {
          const h = (d.count / max) * 100;
          return (
            <div key={d.rating} className="flex-1 flex flex-col items-center justify-end gap-1" data-testid={`hist-bar-${d.rating}`}>
              <span className="text-[11px] font-mono text-foreground/60">{d.count}</span>
              <div className="w-full bg-primary" style={{ height: `${h}%`, minHeight: d.count ? "4px" : "0" }} />
              <div className="flex items-center gap-0.5 mt-1">
                {Array.from({ length: d.rating }).map((_, i) => (
                  <Star key={i} className="w-2.5 h-2.5 fill-amber-500 text-amber-500" />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-foreground/55 text-center mt-3">{totalRated} rated record{totalRated === 1 ? "" : "s"}</p>
    </div>
  );
}

// ───────────────────────── All skills ─────────────────────────

function AllSkillsTab({ wsId, canApprove }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("__all");
  const [categories, setCategories] = useState([]);
  const [decisionFor, setDecisionFor] = useState(null);

  const load = () => {
    if (!wsId) return;
    const params = new URLSearchParams();
    if (status && status !== "all") params.set("status", status);
    if (category && category !== "__all") params.set("category", category);
    if (q.trim()) params.set("q", q.trim());
    api.get(`/workspaces/${wsId}/skills?${params.toString()}`).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId, status, category]);
  useEffect(() => { api.get(`/skill-categories`).then((r) => setCategories(r.data.categories)); }, []);

  return (
    <div className="space-y-4" data-testid="skill-center-all">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50" />
          <Input className="pl-9 rounded-none h-9" placeholder="Search skill name…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") load(); }} data-testid="skill-center-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44 rounded-none h-9" data-testid="skill-center-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending_approval">Pending</SelectItem>
            <SelectItem value="edit_pending">Edit pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-52 rounded-none h-9" data-testid="skill-center-category-filter"><SelectValue placeholder="All categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All categories</SelectItem>
            {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!rows ? (
        <Card className="rounded-none"><CardContent className="p-6 text-sm text-foreground/60">Loading…</CardContent></Card>
      ) : (
        <AdvancedTable
          tableKey="skill-center-all"
          title="All skills"
          rows={rows}
          columns={[
            {
              key: "owner", label: "Owner", width: 220, sortable: true, filterType: "text",
              accessor: (r) => r.owner?.name || r.owner?.email || "",
              render: (r) => (
                <div className="min-w-0">
                  <div className="truncate font-medium" data-testid={`sc-owner-${r.id}`}>{r.owner?.name || r.owner?.email}</div>
                  <div className="text-[11px] text-foreground/55 truncate">{r.owner?.email}</div>
                </div>
              ),
            },
            {
              key: "skill_name", label: "Skill", width: 220, sortable: true, filterType: "text",
              accessor: (r) => r.skill_name || "",
              render: (r) => <span className="font-medium truncate" data-testid={`sc-skill-${r.id}`}>{r.skill_name}</span>,
            },
            {
              key: "category", label: "Category", width: 160, sortable: true, filterType: "enum",
              filterOptions: categories, accessor: (r) => r.category || "",
              render: (r) => <span className="text-xs truncate">{r.category}</span>,
            },
            {
              key: "self_rating", label: "Self", width: 100, sortable: true, filterType: "number-range",
              accessor: (r) => r.self_rating || 0,
              render: (r) => <RatingStars value={r.self_rating} label="" max={5} />,
            },
            {
              key: "manager_rating", label: "Mgr", width: 100, sortable: true, filterType: "number-range",
              accessor: (r) => r.manager_rating || 0,
              render: (r) => r.manager_rating ? <RatingStars value={r.manager_rating} label="" max={5} /> : <span className="text-foreground/30 text-xs">—</span>,
            },
            {
              key: "status", label: "Status", width: 130, sortable: true, filterType: "enum",
              filterOptions: ["pending_approval", "edit_pending", "approved", "rejected"],
              accessor: (r) => r.status || "",
              render: (r) => <StatusBadge status={r.status} />,
            },
          ]}
          emptyIcon={Layers}
          emptyTitle="No skill records match"
          emptyDescription="Try adjusting your filters or search."
          onRowClick={(r) => setDecisionFor({ ...r, _viewOnly: !canApprove || !(r.status === "pending_approval" || r.status === "edit_pending") })}
          renderRowActions={(r) => (
            canApprove && (r.status === "pending_approval" || r.status === "edit_pending") ? (
              <Button size="sm" className="rounded-none h-7 px-2 text-[11px]" onClick={(e) => { e.stopPropagation(); setDecisionFor(r); }} data-testid={`sc-decide-${r.id}`}>Review</Button>
            ) : (
              <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-[11px]" onClick={(e) => { e.stopPropagation(); setDecisionFor({ ...r, _viewOnly: true }); }} data-testid={`sc-view-${r.id}`}>View</Button>
            )
          )}
        />
      )}

      {decisionFor && (
        <ApprovalDialog
          row={decisionFor}
          onClose={() => setDecisionFor(null)}
          onActed={() => { setDecisionFor(null); load(); }}
          canApprove={canApprove && !decisionFor._viewOnly}
        />
      )}
    </div>
  );
}

// ───────────────────────── Approvals queue ─────────────────────────

function ApprovalsTab({ wsId, canApprove }) {
  const [rows, setRows] = useState(null);
  const [decisionFor, setDecisionFor] = useState(null);
  const load = () => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/skills?status=pending_approval`).then((r1) => {
      api.get(`/workspaces/${wsId}/skills?status=edit_pending`).then((r2) => setRows([...r1.data, ...r2.data]));
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId]);

  if (!canApprove) {
    return <Card className="rounded-none"><CardContent className="p-6 text-sm text-foreground/70" data-testid="approvals-forbidden">You need the <code>skill.approve</code> permission to review skill records. Ask a workspace admin.</CardContent></Card>;
  }
  if (!rows) return <Card className="rounded-none"><CardContent className="p-6 text-sm text-foreground/60">Loading…</CardContent></Card>;
  if (rows.length === 0) {
    return <Card className="rounded-none border-dashed"><CardContent className="p-10 text-center text-sm text-foreground/60" data-testid="approvals-empty">All caught up — nothing pending.</CardContent></Card>;
  }

  return (
    <div className="space-y-3" data-testid="approvals-list">
      <p className="text-xs text-foreground/60">{rows.length} skill record{rows.length === 1 ? "" : "s"} waiting on you.</p>
      {rows.map((r) => (
        <Card key={r.id} className="rounded-none" data-testid={`approval-row-${r.id}`}>
          <CardContent className="p-4 flex items-start gap-4">
            <div className="w-9 h-9 bg-foreground/10 flex items-center justify-center text-sm font-bold rounded-none">{(r.owner?.name || "?").slice(0, 1).toUpperCase()}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold">{r.skill_name}</span>
                <span className="text-[11px] text-foreground/55">·</span>
                <span className="text-[11px] text-foreground/65">{r.category}</span>
                <StatusBadge status={r.status} />
              </div>
              <div className="text-xs text-foreground/65 mt-0.5"><strong>{r.owner?.name || r.owner?.email}</strong> · {r.total_experience_months} mo · self {r.self_rating}★ ({r.self_rating_label})</div>
              {r.comments && <p className="mt-2 text-[12px] text-foreground/70 whitespace-pre-wrap">{r.comments}</p>}
              {r.status === "edit_pending" && r.pending_edit && (
                <div className="mt-2 text-[11px] border border-amber-500/30 bg-amber-50 dark:bg-amber-950 p-2" data-testid={`approval-pending-edit-${r.id}`}>
                  <strong>Pending edit:</strong> {Object.entries(r.pending_edit).filter(([k]) => !["status", "updated_at", "pending_edit"].includes(k)).map(([k, v]) => `${k}=${String(v)}`).join(" · ")}
                </div>
              )}
            </div>
            <Button size="sm" className="rounded-none" onClick={() => setDecisionFor(r)} data-testid={`approval-review-${r.id}`}>Review</Button>
          </CardContent>
        </Card>
      ))}

      {decisionFor && (
        <ApprovalDialog
          row={decisionFor}
          onClose={() => setDecisionFor(null)}
          onActed={() => { setDecisionFor(null); load(); }}
          canApprove
        />
      )}
    </div>
  );
}

// ───────────────────────── Approval dialog ─────────────────────────

function ApprovalDialog({ row, onClose, onActed, canApprove }) {
  const [mode, setMode] = useState(null); // null | "approve" | "reject"
  const [managerRating, setManagerRating] = useState(row.manager_rating || row.self_rating || 3);
  const [comments, setComments] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "approve") {
        await api.post(`/skills/${row.id}/approve`, { manager_rating: managerRating, comments: comments || undefined });
        toast.success("Skill approved");
      } else if (mode === "reject") {
        if (!rejectReason.trim()) { toast.error("A reason is required"); setBusy(false); return; }
        await api.post(`/skills/${row.id}/reject`, { reason: rejectReason });
        toast.success("Skill rejected");
      }
      onActed();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to save decision"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="approval-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Award className="w-4 h-4" />{row.skill_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <DT label="Owner" value={row.owner?.name || row.owner?.email} />
            <DT label="Email" value={row.owner?.email} />
            <DT label="Category" value={row.category} />
            <DT label="Experience" value={`${row.total_experience_months} months`} />
            <DT label="Self rating" value={`${row.self_rating}★ · ${row.self_rating_label}`} />
            <DT label="Last used" value={row.last_used_date || "—"} />
            <DT label="Status" value={<StatusBadge status={row.status} />} />
            <DT label="Created" value={(row.created_at || "").slice(0, 10)} />
          </div>
          {row.comments && (
            <div>
              <div className="label-eyebrow mb-1">Owner comments</div>
              <div className="text-xs border border-border p-3 bg-foreground/5 whitespace-pre-wrap">{row.comments}</div>
            </div>
          )}
          {row.status === "edit_pending" && row.pending_edit && (
            <div>
              <div className="label-eyebrow mb-1">Pending edit overlay</div>
              <pre className="text-[11px] border border-amber-500/30 bg-amber-50 dark:bg-amber-950 p-3 overflow-x-auto" data-testid="approval-dialog-pending-edit">{JSON.stringify(row.pending_edit, null, 2)}</pre>
            </div>
          )}

          {canApprove && (row.status === "pending_approval" || row.status === "edit_pending") && (
            <div className="border-t border-border pt-4">
              {!mode && (
                <div className="flex gap-2">
                  <Button className="rounded-none flex-1" onClick={() => setMode("approve")} data-testid="approval-dialog-approve-btn"><CheckCircle2 className="w-4 h-4 mr-2" />Approve</Button>
                  <Button variant="outline" className="rounded-none flex-1" onClick={() => setMode("reject")} data-testid="approval-dialog-reject-btn"><XCircle className="w-4 h-4 mr-2" />Reject</Button>
                </div>
              )}
              {mode === "approve" && (
                <div className="space-y-3">
                  <div>
                    <Label className="label-eyebrow mb-1.5 inline-block">Manager rating</Label>
                    <StarPicker value={managerRating} onChange={setManagerRating} testid="approval-dialog-mgr-rating" />
                  </div>
                  <div>
                    <Label className="label-eyebrow mb-1.5 inline-block">Notes for the owner (optional)</Label>
                    <Textarea rows={3} value={comments} onChange={(e) => setComments(e.target.value)} className="rounded-none" data-testid="approval-dialog-comments" />
                  </div>
                </div>
              )}
              {mode === "reject" && (
                <div>
                  <Label className="label-eyebrow mb-1.5 inline-block">Reason for rejection</Label>
                  <Textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className="rounded-none" placeholder="What needs to change before this can be approved?" data-testid="approval-dialog-reason" />
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          {mode ? (
            <>
              <Button variant="outline" className="rounded-none" onClick={() => setMode(null)} disabled={busy}>Back</Button>
              <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="approval-dialog-submit">{busy ? "Saving…" : mode === "approve" ? "Approve" : "Reject"}</Button>
            </>
          ) : (
            <Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DT({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-foreground/55">{label}</div>
      <div className="font-medium mt-0.5">{value}</div>
    </div>
  );
}

// ───────────────────────── Skill gap ─────────────────────────

function SkillGapTab({ wsId, canEdit }) {
  const [gap, setGap] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = () => api.get(`/workspaces/${wsId}/skill-gap`).then((r) => setGap(r.data));
  useEffect(() => { if (wsId) load(); /* eslint-disable-next-line */ }, [wsId]);

  if (!gap) return <Card className="rounded-none"><CardContent className="p-6 text-sm text-foreground/60">Loading…</CardContent></Card>;

  return (
    <div className="space-y-5" data-testid="skill-gap">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-1">
          <DashStat icon={Target} label="Targets configured" value={gap.summary.total_targets} testid="gap-stat-total" />
          <DashStat icon={CheckCircle2} label="Targets fully met" value={gap.summary.fully_met} testid="gap-stat-met" />
          <DashStat icon={XCircle} label="Targets with gap" value={gap.summary.with_gap} testid="gap-stat-gap" />
          <DashStat icon={Users} label="Open positions" value={gap.summary.total_open_positions} testid="gap-stat-open" />
        </div>
        {canEdit && (
          <Button className="rounded-none" onClick={() => setEditing(true)} data-testid="skill-gap-edit-btn">
            <Target className="w-4 h-4 mr-2" /> {gap.summary.total_targets > 0 ? "Edit targets" : "Configure targets"}
          </Button>
        )}
      </div>

      {gap.rows.length === 0 ? (
        <Card className="rounded-none border-dashed">
          <CardContent className="p-10 text-center text-sm text-foreground/60 space-y-3" data-testid="gap-empty">
            <Target className="w-10 h-10 mx-auto text-foreground/30" />
            <div className="font-semibold text-foreground">No targets yet.</div>
            <p>Define your team&apos;s target skill matrix — for example &quot;3 Senior React&quot; or &quot;2 GCP Architect&quot; — and we&apos;ll highlight gaps + who&apos;s closest to filling them.</p>
            {canEdit && <Button className="rounded-none mt-2" onClick={() => setEditing(true)} data-testid="gap-empty-cta">Configure targets</Button>}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="gap-rows">
          {gap.rows.map((r, i) => (
            <Card key={i} className="rounded-none" data-testid={`gap-row-${i}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold">{r.target.skill_name}</h4>
                    <div className="text-[11px] text-foreground/55 mt-0.5">
                      {r.target.category && <>{r.target.category} · </>}
                      need {r.target.required_count} at {r.target.min_rating}★+
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-3xl font-bold ${r.gap === 0 ? "text-emerald-600" : "text-amber-600"}`}>
                      {r.gap === 0 ? "✓" : `-${r.gap}`}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-foreground/55">{r.met_count}/{r.target.required_count} met</div>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="mt-3 h-2 bg-foreground/10">
                  <div className={`h-full ${r.gap === 0 ? "bg-emerald-500" : "bg-primary"}`} style={{ width: `${Math.min(100, (r.met_count / r.target.required_count) * 100)}%` }} />
                </div>
                {r.met_users.length > 0 && (
                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-widest text-foreground/55 mb-1">Who has it ({r.met_users.length})</div>
                    <div className="flex flex-wrap gap-1.5" data-testid={`gap-met-${i}`}>
                      {r.met_users.map((u) => (
                        <span key={u.user_id} className="text-[11px] border border-emerald-500/30 bg-emerald-50 dark:bg-emerald-950 px-2 py-1 flex items-center gap-1">
                          {u.name} <span className="text-emerald-700 dark:text-emerald-400 font-mono">{u.rating}★</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {r.closest.length > 0 && r.gap > 0 && (
                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-widest text-foreground/55 mb-1">Closest to filling the gap (one star away)</div>
                    <div className="flex flex-wrap gap-1.5" data-testid={`gap-closest-${i}`}>
                      {r.closest.map((u) => (
                        <span key={u.user_id} className="text-[11px] border border-amber-500/30 bg-amber-50 dark:bg-amber-950 px-2 py-1 flex items-center gap-1">
                          {u.name} <span className="text-amber-700 dark:text-amber-400 font-mono">{u.rating}★</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <SkillTargetsEditor wsId={wsId} initial={gap.rows.map((r) => r.target)} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />
      )}
    </div>
  );
}

function SkillTargetsEditor({ wsId, initial, onClose, onSaved }) {
  const [rows, setRows] = useState(initial?.length ? initial : [{ skill_name: "", category: "", min_rating: 3, required_count: 1 }]);
  const [busy, setBusy] = useState(false);
  const update = (i, k, v) => setRows((r) => r.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const add = () => setRows((r) => [...r, { skill_name: "", category: "", min_rating: 3, required_count: 1 }]);
  const remove = (i) => setRows((r) => r.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true);
    try {
      const cleaned = rows.filter((r) => r.skill_name.trim()).map((r) => ({
        skill_name: r.skill_name.trim(),
        category: r.category || null,
        min_rating: Number(r.min_rating) || 3,
        required_count: Number(r.required_count) || 1,
      }));
      await api.put(`/workspaces/${wsId}/skill-targets`, { targets: cleaned });
      toast.success(`${cleaned.length} target${cleaned.length === 1 ? "" : "s"} saved`);
      onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to save targets"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="skill-targets-dialog">
        <DialogHeader><DialogTitle>Configure skill targets</DialogTitle></DialogHeader>
        <p className="text-xs text-foreground/60 -mt-2 mb-2">Each row is a capability your team needs. We compare it against approved skill records and flag gaps + people who are one star away.</p>
        <div className="space-y-2">
          <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-widest text-foreground/55 font-semibold">
            <div className="col-span-4">Skill name</div>
            <div className="col-span-3">Category (optional)</div>
            <div className="col-span-2">Min rating</div>
            <div className="col-span-2">Required count</div>
            <div className="col-span-1"></div>
          </div>
          {rows.map((row, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`target-row-${i}`}>
              <Input value={row.skill_name} onChange={(e) => update(i, "skill_name", e.target.value)} placeholder="e.g. React" className="col-span-4 h-9 rounded-none" data-testid={`target-name-${i}`} />
              <Input value={row.category || ""} onChange={(e) => update(i, "category", e.target.value)} placeholder="Technical" className="col-span-3 h-9 rounded-none" data-testid={`target-category-${i}`} />
              <Select value={String(row.min_rating)} onValueChange={(v) => update(i, "min_rating", Number(v))}>
                <SelectTrigger className="col-span-2 h-9 rounded-none" data-testid={`target-rating-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}★+</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" min={1} max={1000} value={row.required_count} onChange={(e) => update(i, "required_count", e.target.value)} className="col-span-2 h-9 rounded-none" data-testid={`target-count-${i}`} />
              <Button variant="ghost" size="sm" className="col-span-1 rounded-none h-9 text-destructive hover:text-destructive" onClick={() => remove(i)} data-testid={`target-remove-${i}`}>×</Button>
            </div>
          ))}
          <Button variant="outline" className="rounded-none w-full mt-2" onClick={add} data-testid="target-add-row">+ Add target</Button>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="skill-targets-save">{busy ? "Saving…" : "Save targets"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
